// convex/lib/config/domains/system.ts

import { validatorCompany } from "../../../companies";
import { validatorBranch } from "../../../companies/branches";
import { validatorNomenclatureConfig } from "../../../companies/configuration/nomenclatures";
import { validatorWaitlist } from "../../../system/waitlist";
import { validatorUsers } from "../../../system/users";
import { validatorCompanySubscription } from "../../../companies/subscriptions";
import { validatorSubscriptionPlan } from "../../../system/plans";
import type { DomainGroup, TableScope } from "../types";

export const systemDomain: DomainGroup = {
  id: "system",
  name: "Sistema",
  description: "Configuración y administración del sistema",
  icon: "Settings",
  order: 100,
  status: "active",
  modules: [
    {
      id: "control_panel",
      name: "Panel de Control",
      description: "Gestión avanzada de empresas y suscripciones",
      icon: "LayoutDashboard",
      permissions: ["canRead", "canUpdate"],
      routes: ["/super/control-panel"],
      navigation: {
        showInNav: true,
        contexts: ["super"],
        navOrder: 1,
        status: "active",
      },
      tables: [
        {
          tableName: "companies",
          schema: validatorCompany,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name", "email", "phone", "taxId"],
          hasStatusIndex: false,
          searchIndex: { name: "search_companies", field: "name" },
        },
        {
          tableName: "company_subscriptions",
          schema: validatorCompanySubscription,
          scope: "company" as TableScope,
          relations: [
            {
              key: "company",
              sourceField: "companyId",
              schema: validatorCompany,
              tableName: "companies",
            },
            {
              key: "plan",
              sourceField: "planId",
              schema: validatorSubscriptionPlan,
              tableName: "subscription_plans",
            },
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["status"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [
        { tableName: "subscription_plans" },
        { tableName: "users" },
        { tableName: "branches" },
      ],
    },
    {
      id: "companies",
      name: "Empresas",
      description: "Gestión de empresas y configuración general",
      icon: "Building2",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/system/companies", "/super/companies"],
      navigation: {
        showInNav: true,
        contexts: ["super"],
        navOrder: 3,
        status: "active",
      },
      tables: [
        {
          tableName: "companies",
          schema: validatorCompany,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name", "email", "phone", "taxId"],
          hasStatusIndex: false,
          searchIndex: { name: "search_companies", field: "name" },
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "branches",
      name: "Sucursales",
      description: "Configuración de sucursales",
      icon: "MapPin",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/branches"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 1,
        status: "active",
      },
      tables: [
        {
          tableName: "branches",
          schema: validatorBranch,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
            {
              key: "company",
              schema: validatorCompany,
              tableName: "companies",
            },
          ],
          searchFields: ["name", "code", "address", "phone"],
          hasStatusIndex: true,
        },
      ],
      readOnlyTables: [{ tableName: "companies" }],
    },
    {
      id: "nomenclature",
      name: "Nomenclatura",
      description: "Configuración de nomenclaturas y numeración",
      icon: "BookMarked",
      permissions: ["canCreate", "canRead", "canUpdate", "canDelete"],
      routes: ["/settings/nomenclatures"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 4,
        status: "active",
      },
      tables: [
        {
          tableName: "nomenclature",
          schema: validatorNomenclatureConfig,
          scope: "company" as TableScope,
          relations: [
            {
              key: "lastUpdatedBy",
              sourceField: "internalControl.updatedBy",
              schema: validatorUsers,
              tableName: "users",
            },
          ],
          searchFields: ["name", "table", "prefix"],
          hasStatusIndex: false,
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "waitlist",
      name: "Lista de Espera",
      description: "Gestion de solicitudes de acceso y referidos",
      icon: "ClipboardList",
      permissions: ["canRead", "canUpdate"],
      routes: ["/super/waitlist"],
      navigation: {
        showInNav: true,
        contexts: ["super"],
        navOrder: 8,
        status: "active",
      },
      tables: [
        {
          tableName: "waitlist",
          schema: validatorWaitlist,
          scope: "company" as TableScope,
          relations: [],
          searchFields: ["name", "email", "company", "referralCode"],
          hasStatusIndex: true,
          searchIndex: { name: "search_waitlist", field: "name" },
        },
      ],
      readOnlyTables: [],
    },
    {
      id: "deleted_records",
      name: "Papelera",
      description: "Registros eliminados que pueden ser restaurados",
      icon: "Trash2",
      permissions: ["canRead", "canUpdate", "canDelete"],
      routes: ["/settings/trash"],
      navigation: {
        showInNav: true,
        contexts: ["settings"],
        navOrder: 99,
        status: "active",
      },
      tables: [],
      readOnlyTables: [
        { tableName: "nomenclature" },
        { tableName: "packages" },
      ],
    },
  ],
};
